if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.setupAddOns();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const mainVariantId = formData.get('id');
        const addOnInputs = this.getSelectedAddOnInputs();
        const addOnPayload = this.buildAddOnPayload(formData, addOnInputs);

        if (this.cart) {
          const sections = this.cart.getSectionsToRender().map((section) => section.id);
          if (addOnPayload) {
            addOnPayload.sections = sections;
            addOnPayload.sections_url = window.location.pathname;
          } else {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }
          this.cart.setActiveElement(document.activeElement);
        }

        if (addOnPayload) {
          config.body = JSON.stringify(addOnPayload);
        } else {
          delete config.headers['Content-Type'];
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: mainVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: mainVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      setupAddOns() {
        this.addOnCards = Array.from(this.form.querySelectorAll('[data-product-whisker-accessory]'));
        if (!this.addOnCards.length) return;

        this.addOnCards.forEach((card) => {
          const toggle = card.querySelector('[data-product-whisker-accessory-toggle]');
          const input = card.querySelector('[data-product-whisker-accessory-input]');
          if (!toggle || !input) return;

          toggle.addEventListener('click', () => {
            if (input.disabled) return;
            input.checked = !input.checked;
            this.syncAddOnCard(card);
          });

          input.addEventListener('change', () => this.syncAddOnCard(card));
          this.syncAddOnCard(card);
        });
      }

      syncAddOnCard(card) {
        const input = card.querySelector('[data-product-whisker-accessory-input]');
        const toggle = card.querySelector('[data-product-whisker-accessory-toggle]');
        const label = card.querySelector('[data-product-whisker-accessory-toggle-label]');
        const icon = card.querySelector('.product-whisker__accessory-toggle-icon');
        if (!input || !toggle || !label || !icon) return;

        const selected = input.checked && !input.disabled;
        card.classList.toggle('product-whisker__accessory--selected', selected);
        label.textContent = selected ? toggle.dataset.addedLabel || 'Added' : toggle.dataset.addLabel || 'Add';
        icon.textContent = selected ? '\u2713' : '+';
        toggle.setAttribute('aria-pressed', selected ? 'true' : 'false');
      }

      getSelectedAddOnInputs() {
        return Array.from(this.form.querySelectorAll('[data-product-add-on-input]:checked:not(:disabled)')).filter(
          (input) => input.value
        );
      }

      buildAddOnPayload(formData, addOnInputs) {
        if (!addOnInputs.length) return null;

        const mainVariantId = Number(formData.get('id'));
        if (!mainVariantId) return null;

        const mainQuantity = Math.max(1, Number(formData.get('quantity')) || 1);
        const items = [{ id: mainVariantId, quantity: mainQuantity }];

        addOnInputs.forEach((input) => {
          const variantId = Number(input.value);
          if (!variantId) return;

          const followsMainQuantity = input.dataset.followQuantity === 'true';
          const fixedQuantity = Math.max(1, Number(input.dataset.quantity) || 1);
          items.push({
            id: variantId,
            quantity: followsMainQuantity ? mainQuantity : fixedQuantity,
          });
        });

        return { items };
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
